import React, { useEffect, useState } from 'react';
import { api, useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Download, Filter, Search, Edit, Trash2 } from 'lucide-react';
import { exportToExcel, formatExpensesForExport } from '../utils/excelExport';

const ownerExpenseCategories = [
  'Rent',
  'Electricity',
  'Staff Salaries',
  'Marketing',
  'Legal & Professional',
  'Insurance',
  'Taxes',
  'Equipment Purchase',
  'Renovation',
  'Other'
];

export default function OwnerExpenses() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [filters, setFilters] = useState({
    branch_id: 'all',
    category: 'all',
    start_date: '',
    end_date: '',
    search: ''
  });
  const [formData, setFormData] = useState({
    category: '',
    amount: '',
    description: '',
    payment_mode: 'cash',
    date: new Date().toISOString().split('T')[0],
    branch_id: null
  });

  useEffect(() => {
    fetchExpenses();
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
    } catch (error) {
      toast.error('Failed to fetch branches');
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await api.get('/owner-expenses');
      setExpenses(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        branch_id: formData.branch_id === 'none' ? null : formData.branch_id
      };
      
      if (editingExpense) {
        await api.put(`/owner-expenses/${editingExpense.id}`, payload);
        toast.success('Owner expense updated successfully');
      } else {
        await api.post('/owner-expenses', payload);
        toast.success('Owner expense added successfully');
      }
      
      setDialogOpen(false);
      setEditingExpense(null);
      setFormData({
        category: '',
        amount: '',
        description: '',
        payment_mode: 'cash',
        date: new Date().toISOString().split('T')[0],
        branch_id: null
      });
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingExpense ? 'update' : 'add'} expense`);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category,
      amount: expense.amount,
      description: expense.description,
      payment_mode: expense.payment_mode,
      date: expense.date,
      branch_id: expense.branch_id || 'none'
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/owner-expenses/${expenseToDelete.id}`);
      toast.success('Owner expense deleted successfully');
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleExport = () => {
    try {
      const formattedData = formatExpensesForExport(filteredExpenses);
      exportToExcel(formattedData, 'owner_expenses', 'Owner Expenses');
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const applyFilters = () => {
    fetchExpenses();
  };

  const filteredExpenses = expenses.filter(expense => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      if (!expense.description.toLowerCase().includes(search) && 
          !expense.category.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (filters.branch_id && filters.branch_id !== 'all') {
      if (expense.branch_id !== filters.branch_id) return false;
    }
    if (filters.category && filters.category !== 'all') {
      if (expense.category !== filters.category) return false;
    }
    if (filters.start_date && expense.date < filters.start_date) {
      return false;
    }
    if (filters.end_date && expense.date > filters.end_date) {
      return false;
    }
    return true;
  });

  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }} data-testid="owner-expenses-title">
            Owner Expenses
          </h1>
          <p className="text-slate-600 mt-1">Confidential expenses - Admin only</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} variant="outline" className="gap-2" disabled={filteredExpenses.length === 0}>
            <Download size={16} />
            Export to Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingExpense(null);
              setFormData({
                category: '',
                amount: '',
                description: '',
                payment_mode: 'cash',
                date: new Date().toISOString().split('T')[0],
                branch_id: null
              });
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-owner-expense-btn" className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white gap-2">
                <Plus size={16} />
                Add Expense
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingExpense ? 'Edit Owner Expense' : 'Add Owner Expense'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })} required>
                  <SelectTrigger data-testid="owner-expense-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {ownerExpenseCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="branch">Related Branch (Optional)</Label>
                <Select value={formData.branch_id || 'none'} onValueChange={(value) => setFormData({ ...formData, branch_id: value === 'none' ? null : value })}>
                  <SelectTrigger data-testid="owner-expense-branch-select">
                    <SelectValue placeholder="Select branch (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific branch</SelectItem>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  data-testid="owner-expense-amount-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  data-testid="owner-expense-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  data-testid="owner-expense-date-input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Payment Mode *</Label>
                <RadioGroup value={formData.payment_mode} onValueChange={(value) => setFormData({ ...formData, payment_mode: value })}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="cash" id="owner_cash" data-testid="owner-expense-payment-cash" />
                    <Label htmlFor="owner_cash" className="cursor-pointer">Cash</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="online" id="owner_online" data-testid="owner-expense-payment-online" />
                    <Label htmlFor="owner_online" className="cursor-pointer">Online</Label>
                  </div>
                </RadioGroup>
              </div>

              <Button data-testid="submit-owner-expense-btn" type="submit" className="w-full">
                {editingExpense ? 'Update Expense' : 'Add Expense'}
              </Button>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter size={20} />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  placeholder="Search description..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Branch</Label>
              <Select value={filters.branch_id} onValueChange={(value) => setFilters({ ...filters, branch_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="All branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All branches</SelectItem>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={filters.category} onValueChange={(value) => setFilters({ ...filters, category: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {ownerExpenseCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              />
            </div>

            <div className="flex items-end gap-2">
              <Button onClick={applyFilters} className="flex-1">
                Apply Filters
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setFilters({ branch_id: 'all', category: 'all', start_date: '', end_date: '', search: '' })}
                className="flex-1"
              >
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="text-amber-900">Total Owner Expenses: ₹{filteredExpenses.reduce((sum, e) => sum + e.amount, 0).toFixed(2)}</CardTitle>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-slate-600">Loading expenses...</p>
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No owner expenses found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredExpenses.map((expense) => (
                <div
                  key={expense.id}
                  data-testid={`owner-expense-${expense.id}`}
                  className="p-4 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{expense.category}</h3>
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 capitalize">
                          {expense.payment_mode}
                        </span>
                        {expense.branch_name && (
                          <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                            {expense.branch_name}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{expense.description}</p>
                      <div className="flex gap-4 mt-2 text-xs text-slate-500">
                        <span>Date: {expense.date}</span>
                      </div>
                    </div>
                    <div className="text-right flex items-start gap-2">
                      <div>
                        <p className="text-lg font-bold text-slate-900">₹{expense.amount.toFixed(2)}</p>
                      </div>
                      {user?.role === 'admin' && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(expense)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setExpenseToDelete(expense);
                              setDeleteDialogOpen(true);
                            }}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Owner Expense</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Are you sure you want to delete this expense? This action cannot be undone.
          </p>
          {expenseToDelete && (
            <div className="p-3 bg-slate-50 rounded-lg mt-2">
              <p className="text-sm"><strong>Category:</strong> {expenseToDelete.category}</p>
              <p className="text-sm"><strong>Amount:</strong> ₹{expenseToDelete.amount.toFixed(2)}</p>
              <p className="text-sm"><strong>Description:</strong> {expenseToDelete.description}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
